/**
 * Safely parses a JSON string or returns the value if already an object.
 * Handles double-encoded strings. Returns fallback on failure.
 */
import { getBearerToken } from 'application/common/utils/authUtils';

export function safeParseJson<T = Record<string, any>>(
  value: unknown,
  fallback: T
): T {
  if (!value) return fallback;
  if (typeof value === 'object' && !Array.isArray(value)) return value as T;

  try {
    let parsed = typeof value === 'string' ? JSON.parse(value) : value;
    // Handle double-encoded strings
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as T;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * Picks the best integration config from a list:
 * prefers active, then most recently created.
 */
export function pickBestConfig<
  T extends { isActive?: boolean; createdAt?: string }
>(items: T[]): T | null {
  if (!items.length) return null;

  const sorted = [...items].sort((a, b) =>
    String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))
  );

  return sorted.find((it) => it.isActive === true) ?? sorted[0];
}

/**
 * Gets a fresh Cognito access token for API Gateway endpoints.
 */
export async function getAccessToken(): Promise<string> {
  const token = await getBearerToken();
  if (token) return token;

  const existing = (window as any)?.me?.accessToken;
  if (existing) return existing;

  throw new Error(
    'Authentication token not found. Please refresh the page and try again.'
  );
}

/**
 * Builds headers for the new JN config API.
 * Uses ID token with Bearer prefix — matching the pattern used by other working API Gateway endpoints.
 */
async function jnConfigHeaders(): Promise<Record<string, string>> {
  const session = await (await import('aws-amplify')).Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Sends a PATCH request to partially update an integration config.
 * Used for enable/disable (isActive toggling).
 */
export async function patchIntegrationConfig(
  baseUrl: string,
  id: string,
  patch: Record<string, any>
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${baseUrl}/${id}`, {
    method: 'PATCH',
    headers: await jnConfigHeaders(),
    body: JSON.stringify(patch),
  });

  const result = await response.json();

  if (response.ok && result?.success) {
    return { success: true };
  }

  return {
    success: false,
    error: result?.error || result?.message || `HTTP ${response.status}`,
  };
}

/**
 * Sends a DELETE request to soft-delete an integration config.
 */
export async function deleteIntegrationConfig(
  baseUrl: string,
  id: string
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${baseUrl}/${id}`, {
    method: 'DELETE',
    headers: await jnConfigHeaders(),
  });

  const result = await response.json();

  if (response.ok && result?.success) {
    return { success: true };
  }

  return {
    success: false,
    error: result?.error || result?.message || `HTTP ${response.status}`,
  };
}

/**
 * Creates a new integration config via POST.
 */
export async function createIntegrationConfig(
  baseUrl: string,
  payload: Record<string, any>
): Promise<{ success: boolean; id?: string; error?: string }> {
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: await jnConfigHeaders(),
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (response.ok && result?.success) {
    return { success: true, id: result.id };
  }

  return {
    success: false,
    error: result?.error || result?.message || `HTTP ${response.status}`,
  };
}

/**
 * Full update of an integration config via PUT.
 */
export async function updateIntegrationConfig(
  baseUrl: string,
  id: string,
  payload: Record<string, any>
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${baseUrl}/${id}`, {
    method: 'PUT',
    headers: await jnConfigHeaders(),
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (response.ok && result?.success) {
    return { success: true };
  }

  return {
    success: false,
    error: result?.error || result?.message || `HTTP ${response.status}`,
  };
}

/**
 * Lists integration configs by orgID from the new REST endpoint.
 * GET /integrations/jobnimbus/config?orgID=xxx
 */
export async function listIntegrationConfigs(
  baseUrl: string,
  orgID: string
): Promise<{ success: boolean; items?: any[]; error?: string }> {
  const response = await fetch(
    `${baseUrl}?orgID=${encodeURIComponent(orgID)}`,
    {
      method: 'GET',
      headers: await jnConfigHeaders(),
    }
  );

  const result = await response.json();

  if (response.ok) {
    return { success: true, items: result.items ?? [] };
  }

  return {
    success: false,
    error: result?.error || result?.message || `HTTP ${response.status}`,
  };
}
